import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  cn,
} from '@design-system';
import {
  formatInteger as sharedFormatInteger,
  formatThousands,
  isIntegerOrNull,
  isPercentOrNull,
  isRealNumber,
  isRealNumberOrNull,
  parseInteger,
  parseWholeNumberToPercent,
} from '@shared';
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

/**
 * Design-system numeric inputs that replace the legacy MUI-based
 * NumericInput / CurrencyInput / PercentInput / IntegerInput. API matches the
 * legacy components so callers can swap drop-in:
 *   - `value` is a number | null
 *   - `onChange` fires when the field commits (blur with a valid value)
 *   - `onPreview` fires on every keystroke while a valid value is typed
 *   - `errorMessage` renders below the input
 */

export interface NumericInputProps {
  label?: ReactNode;
  placeholder?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onPreview?: (value: number | null) => void;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  /** Optional id; if omitted the label is wrapped around the input. */
  id?: string;
  /** Currency `$` / percent `%` adornment. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Format a numeric value for display in the input. */
  formatter?: (value: number | null) => string;
  /** Parse a raw string back into a number (or null when blank/invalid). */
  parser?: (raw: string) => number | null;
  /** Validate parsed values; commits/previews skip when this returns false. */
  validator?: (value: number | null) => boolean;
}

function defaultFormatter(value: number | null) {
  if (!isRealNumber(value)) return '';
  // Trim trailing fractional zeros so $25,000.00 displays as $25,000.
  const text = String(value);
  const dotIndex = text.indexOf('.');
  if (dotIndex === -1) return text;
  if (text.substring(dotIndex, dotIndex + 5) === '.0000') return text.substring(0, dotIndex);
  return text.length > dotIndex + 3
    ? (Math.round(value * 100) / 100).toString()
    : text;
}

function defaultParser(raw: string): number | null {
  if (raw === '') return null;
  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export function NumericInput({
  label,
  placeholder,
  value,
  onChange,
  onPreview,
  errorMessage,
  disabled,
  className,
  inputClassName,
  id,
  prefix,
  suffix,
  formatter = defaultFormatter,
  parser = defaultParser,
  validator = isRealNumberOrNull,
}: NumericInputProps) {
  const [editingValue, setEditingValue] = useState(() => formatter(value));
  const [hasFocus, setHasFocus] = useState(false);
  const [lastIncoming, setLastIncoming] = useState(value);
  const [isDirty, setIsDirty] = useState(false);
  const [validValue, setValidValue] = useState<number | null>(value);
  const [isInvalid, setIsInvalid] = useState(false);

  // Sync the input display when the upstream value changes. Normally this only
  // fires while the field isn't focused so we don't clobber what the user is
  // typing — but when the field is autofocused on mount (e.g. Radix Dialog
  // focusing the first input) the user hasn't started typing yet, so we also
  // sync if `editingValue` still matches the formatted previous value.
  useEffect(() => {
    if (value === lastIncoming) return;
    const userIsEditing = hasFocus && editingValue !== formatter(lastIncoming);
    if (userIsEditing) return;
    setLastIncoming(value);
    setEditingValue(formatter(value));
    setValidValue(value);
    setIsInvalid(false);
  }, [value, lastIncoming, hasFocus, editingValue, formatter]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // Allow only digits, decimals, separators, and currency/percent symbols.
      const raw = event.target.value.replace(/[^0-9.,$%+\-]/g, '');
      setEditingValue(raw);

      const cleaned = raw.replace(/[$+,% ]/g, '');
      const parsed = cleaned.length === 0 ? null : parser(cleaned);
      const valid = !Number.isNaN(parsed) && validator(parsed);
      setIsInvalid(!valid);

      if (!valid) return;
      setValidValue(parsed);
      setIsDirty(parsed !== value);
      onPreview?.(parsed);
    },
    [parser, validator, onPreview, value],
  );

  const handleFocus = useCallback((_event: FocusEvent<HTMLInputElement>) => {
    setHasFocus(true);
    setIsInvalid(false);
  }, []);

  const handleBlur = useCallback(() => {
    setHasFocus(false);
    if (!isInvalid && isDirty) {
      onChange(validValue);
      setIsDirty(false);
    }
    // Re-format the display from the current canonical value.
    setEditingValue(formatter(isInvalid ? value : validValue));
  }, [formatter, isDirty, isInvalid, onChange, validValue, value]);

  const inputElement = (
    <Input
      id={id}
      placeholder={placeholder}
      value={editingValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      aria-invalid={isInvalid || (!!errorMessage && !hasFocus)}
      className={inputClassName}
      inputMode="decimal"
    />
  );

  // When prefix/suffix is provided we use InputGroup so the adornment lives
  // inside the input border.
  const control =
    prefix || suffix ? (
      <InputGroup>
        {prefix && <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>}
        <InputGroupInput
          id={id}
          placeholder={placeholder}
          value={editingValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          aria-invalid={isInvalid || (!!errorMessage && !hasFocus)}
          inputMode="decimal"
        />
        {suffix && <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon>}
      </InputGroup>
    ) : (
      inputElement
    );

  return (
    <div className={cn('space-y-1', className)}>
      {label != null && (
        <Label
          htmlFor={id}
          className="block text-xs font-medium"
        >
          {label}
        </Label>
      )}
      {control}
      {errorMessage && !isInvalid && (
        <p className="text-destructive text-xs">{errorMessage}</p>
      )}
    </div>
  );
}

function formatCurrencyForInput(value: number | null) {
  return value == null ? '' : formatThousands(value, '', 2);
}

function formatPercentForInput(value: number | null) {
  if (!isRealNumber(value)) return '';
  // Stored values are decimals (0.04 == 4%). Display as the whole-number
  // percentage so the user types "4" and sees "4".
  const asWholePercent = Number((value * 100).toFixed(4));
  return formatThousands(asWholePercent, '', 4);
}

export function CurrencyInput(props: NumericInputProps) {
  return (
    <NumericInput
      formatter={formatCurrencyForInput}
      parser={Number.parseFloat}
      validator={isRealNumberOrNull}
      prefix="$"
      {...props}
    />
  );
}

export function PercentInput(props: NumericInputProps) {
  return (
    <NumericInput
      formatter={formatPercentForInput}
      parser={parseWholeNumberToPercent}
      validator={isPercentOrNull}
      suffix="%"
      {...props}
    />
  );
}

export function IntegerInput(props: NumericInputProps) {
  return (
    <NumericInput
      formatter={sharedFormatInteger}
      parser={parseInteger}
      validator={isIntegerOrNull}
      {...props}
    />
  );
}
